import { useState, useEffect } from 'react'
import { useCreateSegment, useSegments } from '../../hooks/useSegments'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'

interface AddSegmentDialogProps {
  runId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultDistance?: string
}

export function AddSegmentDialog({ runId, open, onOpenChange, defaultDistance }: AddSegmentDialogProps) {
  const createMutation = useCreateSegment()
  const { data: segments } = useSegments(runId)
  const [formData, setFormData] = useState({
    label: '',
    distance_km: defaultDistance || '',
    elev_gain_m: '',
    elev_loss_m: '',
    planned_stop_min: '0'
  })
  const [addAnother, setAddAnother] = useState(false)

  // Reset form and checkbox when dialog first opens
  useEffect(() => {
    if (open) {
      setFormData({
        label: '',
        distance_km: defaultDistance || '',
        elev_gain_m: '',
        elev_loss_m: '',
        planned_stop_min: '0'
      })
      // Only reset addAnother on first open, not when staying open
      if (!addAnother) {
        setAddAnother(false)
      }
    } else {
      // Reset checkbox when dialog is actually closed
      setAddAnother(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDistance]) // Removed addAnother from dependencies to avoid loops

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const nextOrderIndex = (segments?.length || 0) + 1

    try {
      await createMutation.mutateAsync({
        run_id: runId,
        label: formData.label || null,
        distance_km: parseFloat(formData.distance_km),
        elev_gain_m: parseInt(formData.elev_gain_m) || 0,
        elev_loss_m: parseInt(formData.elev_loss_m) || 0,
        planned_stop_min: parseFloat(formData.planned_stop_min) || 0,
        order_index: nextOrderIndex
      })
      
      if (addAnother) {
        // Keep dialog open and reset form with same distance
        setFormData({
          label: '',
          distance_km: formData.distance_km, // Keep the same distance
          elev_gain_m: '',
          elev_loss_m: '',
          planned_stop_min: '0'
        })
        // Keep the checkbox checked - don't reset addAnother state
      } else {
        onOpenChange(false)
        setFormData({
          label: '',
          distance_km: defaultDistance || '',
          elev_gain_m: '',
          elev_loss_m: '',
          planned_stop_min: '0'
        })
        setAddAnother(false) // Only reset checkbox when actually closing dialog
      }
    } catch (error) {
      console.error('Failed to create segment:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Segment</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              value={formData.label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g., Forest Loop, Climb to Peak"
            />
          </div>

          <div>
            <Label htmlFor="distance">Distance (km) *</Label>
            <Input
              id="distance"
              type="number"
              step="0.01"
              min="0.10"
              value={formData.distance_km}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, distance_km: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="elevGain">Elevation Gain (m)</Label>
              <Input
                id="elevGain"
                type="number"
                min="0"
                value={formData.elev_gain_m}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, elev_gain_m: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="elevLoss">Elevation Loss (m)</Label>
              <Input
                id="elevLoss"
                type="number"
                min="0"
                value={formData.elev_loss_m}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, elev_loss_m: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="plannedStop">Planned Stop (minutes)</Label>
            <Input
              id="plannedStop"
              type="number"
              step="0.5"
              min="0"
              value={formData.planned_stop_min}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, planned_stop_min: e.target.value }))}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="addAnother"
              checked={addAnother}
              onCheckedChange={(checked: boolean | "indeterminate") => setAddAnother(checked === true)}
            />
            <Label htmlFor="addAnother" className="text-sm font-normal">
              Add another segment
            </Label>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Adding...' : 'Add Segment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}